/**
 * 計助ダッシュボード用・レガシー棒グラフ（論文用の細かい設定可能）
 * レガシー計助 ANOVAView のグラフ設定を完全移植。
 */

import { useMemo, useRef, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-bootstrap';
import KeisukeChart from '../charts/KeisukeChart';
import KeisukeFreeLegendDragOverlay from '../charts/KeisukeFreeLegendDragOverlay';
import KeisukeDashboardChartSettingsPanel from './KeisukeDashboardChartSettingsPanel';
import KeisukeAxis from '../charts/KeisukeAxis';
import KeisukeBar from '../charts/KeisukeBar';
import {
  createLinearScale,
  calculateNiceTicks,
  calculateTicksByInterval,
  getYDomainForBars,
  getColor,
  shrinkBarCategoryAxisLength,
  resolveCategoryBandStartX,
  normalizeChartFontFamilyValue,
} from '../charts/chartUtils';
import type { ChartDimensions, AxisScale, BarData } from '../charts/types';
import { keisukeStockColumnDisplayLabel } from '../../../../utils/keisukeStockColumnLabels';
import {
  computeGroupBarData,
  computeGroupBoxPlotData,
  computeGroupScatterData,
  getYDomainForBoxPlot,
  getYDomainForScatter,
  buildSignificanceMarks,
  type DashboardBarErrorBarType,
  type DashboardScatterPoint,
} from './keisukeDashboardChartUtils';
import type { DataRow } from '../../../../stores/systems/keisuke/dashboardStore';
import { copySvgElementAsPngToClipboard, normalizeSvgDimensionsForExport } from '../../../../utils/exportUtils';
import { KeisukeDashboardChartExportDropdown } from './KeisukeDashboardChartExportDropdown';
import styles from './KeisukeDashboardChart.module.scss';
import UTIF from 'utif';
import {
  coalesceReferenceLine,
  createDefaultBarChartSettings,
  DEFAULT_DASHBOARD_CHART_HEIGHT_WHEN_AUTO_PX,
  MAX_SPLIT_PLOT_AUTO_HEIGHT_PX,
  normalizeDashboardBarChartYAxisScaleType,
  normalizeMarginAdjust,
} from './keisukeDashboardBarChartSettings';
import type { DashboardBarChartSettings } from './keisukeDashboardBarChartSettings';
import { useBarChartSettings } from './KeisukeDashboardBarChartSettingsContext';
import { useKeisukeChartSettingsUi } from './keisukeChartSettingsUiContext';
import {
  measureSplitPlotHostContentSize,
  resolveSplitPlotSvgHeightPx,
} from './keisukeDashboardSplitPlotSize';
/** 指数表記の Y 目盛など左に長い文字列でもクリップしにくい余裕 */
const DEFAULT_MARGIN = { top: 40, right: 40, bottom: 60, left: 120 };

/** 回転したX目盛ラベルが収まるよう下マージンに足す量（ANOVAView と同ロジック） */
function getXAxisTickLabelHeight(labels: string[], fontSize: number, rotation: number): number {
  if (labels.length === 0) return fontSize;
  const maxLength = Math.max(...labels.map((label) => label.length));
  const textWidth = maxLength * fontSize * 0.6;
  const textHeight = fontSize;
  const radians = (Math.min(90, Math.abs(rotation)) * Math.PI) / 180;
  const height = Math.sin(radians) * textWidth + Math.cos(radians) * textHeight;
  return Math.max(textHeight, height);
}

/**
 * 目盛位置 labelY（テキスト縦中央）と回転を前提に、項目ラベル下端の Y（SVG座標・下方向が増加）
 */
function getCategoryTickLabelsBottomY(
  labelCenterY: number,
  categories: string[],
  fontSize: number,
  rotationDeg: number
): number {
  const h = getXAxisTickLabelHeight(categories, fontSize, rotationDeg);
  return labelCenterY + h / 2;
}

/**
 * 事後検定ブラケットの縦段ピッチ（px）。
 * gap だけだと段差が小さく、上端クランプで複数段が同じ Y に潰れやすいため下限を設ける。
 */
function getDashboardSignificanceRowPitchPx(settings: DashboardBarChartSettings): number {
  const rawGap = Number(settings.significance.gap);
  const fontSize = Number(settings.textStyles.tickLabel.fontSize) || 16;
  const userOrDefault = Number.isFinite(rawGap) && rawGap > 0 ? rawGap : 24;
  const step = Math.max(22, userOrDefault, fontSize + 16);
  return Math.max(56, step + fontSize + 16);
}

function formatExponentForAxis(exp: number): string {
  const superscripts: Record<string, string> = {
    '0': '⁰',
    '1': '¹',
    '2': '²',
    '3': '³',
    '4': '⁴',
    '5': '⁵',
    '6': '⁶',
    '7': '⁷',
    '8': '⁸',
    '9': '⁹',
    '-': '⁻',
  };
  return String(exp)
    .split('')
    .map((char) => superscripts[char] ?? char)
    .join('');
}

function formatExponentialValue(value: number, digits = 1): string {
  if (!Number.isFinite(value)) return '—';
  const [coefficient, exponent] = value.toExponential(digits).split('e');
  const expValue = parseInt(exponent ?? '0', 10);
  return `${coefficient}×10${formatExponentForAxis(expValue)}`;
}

function formatLogTickValue(logValue: number): string {
  const value = Math.pow(10, logValue);
  if (!Number.isFinite(value)) return '—';
  if (value >= 1000 || value < 0.01) {
    return value.toExponential(1).replace('e+', 'e');
  }
  return Number(value.toPrecision(3)).toString();
}

function resolveFontWeightDash(
  style: 'normal' | 'italic' | 'bold',
  weight: 'normal' | 'bold' | '500' | '600' | '700'
): 'normal' | 'bold' | '500' | '600' | '700' {
  return style === 'bold' ? 'bold' : weight;
}

function resolveFontStyleDash(style: 'normal' | 'italic' | 'bold'): 'normal' | 'italic' {
  return style === 'bold' ? 'normal' : style;
}

/** 平方根・逆数軸の目盛が指す「データ空間の Y」の文字列（指数・桁指定・自動丸め） */
function formatYAxisTickRawDataValue(
  yAxis: DashboardBarChartSettings['yAxis'],
  rawValue: number,
  digits: number | null
): string {
  if (!Number.isFinite(rawValue)) return '—';
  if (yAxis.tickLabelFormat === 'exponential') {
    return formatExponentialValue(rawValue, digits ?? 1);
  }
  if (digits !== null && Number.isFinite(digits)) {
    return rawValue.toFixed(digits);
  }
  if (rawValue >= 1000 || (rawValue > 0 && rawValue < 0.01)) {
    return rawValue.toExponential(1).replace('e+', 'e');
  }
  return Number(rawValue.toPrecision(3)).toString();
}

/** Y軸目盛ラベル（対数・平方根・逆数・指数表示・桁数）— ANOVAView と同ロジック */
function createYAxisTickFormatter(
  yAxis: DashboardBarChartSettings['yAxis'],
  useLogScale: boolean,
  useSqrtScale: boolean,
  useInvScale: boolean
): ((v: number) => string) | undefined {
  const digits = yAxis.tickLabelDigits;
  if (useLogScale) {
    return (value: number) => {
      const rawValue = Math.pow(10, value);
      if (yAxis.tickLabelFormat === 'exponential') {
        return formatExponentialValue(rawValue, digits ?? 1);
      }
      if (digits !== null && Number.isFinite(digits)) {
        return rawValue.toFixed(digits);
      }
      return formatLogTickValue(value);
    };
  }
  if (useSqrtScale) {
    return (sqrtTick: number) => {
      const rawValue = sqrtTick * sqrtTick;
      return formatYAxisTickRawDataValue(yAxis, rawValue, digits);
    };
  }
  if (useInvScale) {
    return (invTick: number) => {
      if (!Number.isFinite(invTick) || invTick === 0) return '—';
      const rawValue = 1 / invTick;
      return formatYAxisTickRawDataValue(yAxis, rawValue, digits);
    };
  }
  if (
    yAxis.tickLabelFormat === 'exponential' ||
    (digits !== null && Number.isFinite(digits))
  ) {
    return (value: number) =>
      yAxis.tickLabelFormat === 'exponential'
        ? formatExponentialValue(value, digits ?? 1)
        : value.toFixed(digits ?? 0);
  }
  return undefined;
}

function formatPValue(p: number): string {
  if (!Number.isFinite(p)) return 'NaN';
  if (p <= 0) return '< 1×10⁻¹⁵';
  if (p < 0.001) return '< 0.001';
  if (p < 0.01) return `p = ${p.toFixed(3)}`;
  return `p = ${p.toFixed(2)}`;
}

export type KeisukeDashboardCategoryChartLayout = 'split-settings' | 'split-plot';

interface KeisukeDashboardCategoryChartProps {
  gridData: DataRow[];
  groupCol: string;
  valueCol: string;
  /** ワイド2列表で valueCol が group/value でも、測定名（Weight 等）を軸に出す */
  valueAxisLabel?: string;
  significancePairs?: Array<{ leftIndex: number; rightIndex: number; pValue: number }>;
  isCalculating?: boolean;
  /** split-settings: 中央ペイン下部のグラフ設定のみ。split-plot: 右ペインの図のみ */
  layout: KeisukeDashboardCategoryChartLayout;
  /** PNG/SVG 等のファイル名プレフィックス（qPCR・基本統計など） */
  exportFileBaseName?: string;
}

export default function KeisukeDashboardCategoryChart({
  gridData,
  groupCol,
  valueCol,
  valueAxisLabel,
  significancePairs = [],
  isCalculating = false,
  layout,
  exportFileBaseName = 'keisuke_bar',
}: KeisukeDashboardCategoryChartProps) {
  const { t } = useTranslation();
  const chartWrapRef = useRef<HTMLDivElement>(null);
  const plotHostRef = useRef<HTMLDivElement>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const freeLegendAreaRef = useRef<HTMLDivElement>(null);
  const { chartSettings, setChartSettings } = useBarChartSettings();
  const chartSettingsUi = useKeisukeChartSettingsUi();
  const [autoContainerSize, setAutoContainerSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  /**
   * 右ペインでは保存済みの固定高さを使わない（幅と同様）。
   * 以前は size.height があると HD で上下に砂漠が残った。
   */
  const useContainerHeightForPlot =
    layout === 'split-plot' && chartSettings.size.plotAreaHeight == null;

  useEffect(() => {
    if (!useContainerHeightForPlot) {
      setAutoContainerSize(null);
      return;
    }
    const host = plotHostRef.current;
    if (!host) return;
    const measure = () => {
      const size = measureSplitPlotHostContentSize(host);
      if (size.height > 80) setAutoContainerSize(size);
    };
    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(host);
    return () => ro.disconnect();
  }, [useContainerHeightForPlot]);

  /** フォーム id / name とラベル関連付け（オートフィル・アクセシビリティ） */
  const fid = (suffix: string) => `keisuke-dash-bar-${suffix}`;

  /** 内部キー group/value やよくある測定列名を UI 言語の軸タイトルへ */
  const axisTitleFromColumn = useCallback(
    (col: string, role: 'group' | 'value') => keisukeStockColumnDisplayLabel(col, role, t),
    [t]
  );

  const yAxisSource = valueAxisLabel || valueCol;

  /** 表の列名（項目ラベル）を軸タイトルに合わせる。列名変更・マッピング変更時も追従 */
  useEffect(() => {
    if (!groupCol && !yAxisSource) return;
    setChartSettings((s) => {
      const nextX = groupCol ? axisTitleFromColumn(groupCol, 'group') : s.xAxis.label;
      const nextY = yAxisSource ? axisTitleFromColumn(yAxisSource, 'value') : s.yAxis.label;
      if (s.xAxis.label === nextX && s.yAxis.label === nextY) return s;
      return {
        ...s,
        xAxis: { ...s.xAxis, label: nextX },
        yAxis: { ...s.yAxis, label: nextY },
      };
    });
  }, [groupCol, yAxisSource, setChartSettings, axisTitleFromColumn]);

  const handleResetChartSettings = useCallback(() => {
    const def = createDefaultBarChartSettings(t);
    setChartSettings({
      ...def,
      xAxis: {
        ...def.xAxis,
        label: groupCol ? axisTitleFromColumn(groupCol, 'group') : def.xAxis.label,
      },
      yAxis: {
        ...def.yAxis,
        label: yAxisSource ? axisTitleFromColumn(yAxisSource, 'value') : def.yAxis.label,
      },
    });
  }, [t, setChartSettings, groupCol, yAxisSource, axisTitleFromColumn]);

  const barData = useMemo(
    () =>
      computeGroupBarData(gridData, groupCol, valueCol, {
        errorBarType: chartSettings.groupDisplay.errorBarType,
        barCenter: chartSettings.groupDisplay.barCenter,
      }),
    [gridData, groupCol, valueCol, chartSettings.groupDisplay.errorBarType, chartSettings.groupDisplay.barCenter]
  );

  const categories = useMemo(() => barData.map((d) => d.name), [barData]);

  /** 全群が同色（論文向け黒棒など）のとき凡例は X 軸ラベルと重複するので出さない */
  const legendColorsAreUniform = useMemo(() => {
    if (categories.length === 0) return true;
    const colors = categories.map(
      (name, index) => barData.find((d) => d.name === name)?.color ?? getColor(index)
    );
    return colors.every((c) => c === colors[0]);
  }, [categories, barData]);

  const showCategoryLegend = chartSettings.legend.show && !legendColorsAreUniform;

  /** データの群ラベルが変わったとき、垂直参照のカテゴリが一覧に無ければクリア（categories が空の間は触らない） */
  useEffect(() => {
    if (categories.length === 0) return;
    setChartSettings((s) => {
      const rl = coalesceReferenceLine(s.referenceLine);
      if (!rl.verticalCategory || categories.includes(rl.verticalCategory)) return s;
      return {
        ...s,
        referenceLine: { ...rl, verticalCategory: null },
      };
    });
  }, [categories, setChartSettings]);

  const categoryPlotKind = chartSettings.plot.categoryPlotKind ?? 'bar';

  const boxPlotSeries = useMemo(
    () =>
      categoryPlotKind === 'box'
        ? computeGroupBoxPlotData(gridData, groupCol, valueCol, categories, { legacyBarStyle: true })
        : [],
    [categoryPlotKind, gridData, groupCol, valueCol, categories]
  );

  const scatterPoints = useMemo(
    () =>
      categoryPlotKind === 'scatter'
        ? computeGroupScatterData(gridData, groupCol, valueCol, categories, { legacyStyle: true })
        : ([] as DashboardScatterPoint[]),
    [categoryPlotKind, gridData, groupCol, valueCol, categories]
  );

  const baseYDomain = useMemo(() => {
    if (categoryPlotKind === 'box' && boxPlotSeries.length > 0) {
      return getYDomainForBoxPlot(boxPlotSeries);
    }
    if (categoryPlotKind === 'scatter' && scatterPoints.length > 0) {
      return getYDomainForScatter(scatterPoints);
    }
    return getYDomainForBars(barData);
  }, [categoryPlotKind, boxPlotSeries, scatterPoints, barData]);
  const yDomain: [number, number] = useMemo(() => {
    const minOverride = chartSettings.yAxis.minValue ?? baseYDomain[0];
    const maxOverride = chartSettings.yAxis.maxValue ?? baseYDomain[1];
    return [minOverride, maxOverride];
  }, [baseYDomain, chartSettings.yAxis.minValue, chartSettings.yAxis.maxValue]);

  const significanceMarks = useMemo(
    () =>
      buildSignificanceMarks(significancePairs, {
        displayMode: chartSettings.significance.displayMode,
        topN: chartSettings.significance.topN,
      }),
    [significancePairs, chartSettings.significance.displayMode, chartSettings.significance.topN]
  );

  /** 有意差ブラケット用の上余白（プロットを下げ、クランプで段が潰れないようにする） */
  const significanceTopExtra = useMemo(() => {
    if (!chartSettings.significance.show || significanceMarks.length === 0) return 0;
    const maxLevel = Math.max(...significanceMarks.map((m) => m.level));
    const rowPitch = getDashboardSignificanceRowPitchPx(chartSettings);
    const fs = chartSettings.textStyles.tickLabel.fontSize || 16;
    return chartSettings.significance.offset + maxLevel * rowPitch + fs + 28;
  }, [chartSettings.significance, chartSettings.textStyles.tickLabel.fontSize, significanceMarks]);

  /**
   * 軸線〜項目ラベル下端〜X軸タイトルまでを含め、SVG 下で切れないよう下マージンに加算するpx
   * （従来の tickH-12 だけでは、タイトル用の labelPosition 分が足りない場合がある）
   */
  const xAxisLabelExtraBottom = useMemo(() => {
    const fs = chartSettings.textStyles.tickLabel.fontSize;
    const rot = chartSettings.xAxis.tickLabelRotation;
    const tickH = getXAxisTickLabelHeight(categories, fs, rot);
    const tickLen = 5;
    const labelPad = 15;
    const tickBottomFromAxis = tickLen + labelPad + tickH / 2;
    const hasXTitle = Boolean(chartSettings.xAxis.label?.trim());
    const titleBlock = hasXTitle
      ? chartSettings.xAxis.labelPosition + chartSettings.textStyles.axisLabel.fontSize
      : 0;
    const neededBelowAxis = tickBottomFromAxis + titleBlock + 12;
    return Math.max(0, Math.round(neededBelowAxis - DEFAULT_MARGIN.bottom));
  }, [
    categories,
    chartSettings.textStyles.tickLabel.fontSize,
    chartSettings.xAxis.tickLabelRotation,
    chartSettings.xAxis.label,
    chartSettings.xAxis.labelPosition,
    chartSettings.textStyles.axisLabel.fontSize,
  ]);

  const chartMarginForChart = useMemo(() => {
    const base =
      chartSettings.legend.position === 'right'
        ? {
            ...DEFAULT_MARGIN,
            top: DEFAULT_MARGIN.top + significanceTopExtra,
            right: 10,
            bottom: DEFAULT_MARGIN.bottom + xAxisLabelExtraBottom,
          }
        : {
            ...DEFAULT_MARGIN,
            top: DEFAULT_MARGIN.top + significanceTopExtra,
            bottom: DEFAULT_MARGIN.bottom + xAxisLabelExtraBottom,
          };
    const adj = normalizeMarginAdjust(chartSettings.marginAdjust);
    return {
      top: base.top + adj.top,
      right: base.right + adj.right,
      bottom: base.bottom + adj.bottom,
      left: base.left + adj.left,
    };
  }, [chartSettings.legend.position, chartSettings.marginAdjust, xAxisLabelExtraBottom, significanceTopExtra]);

  const chartWidth = useMemo(() => {
    if (chartSettings.size.plotAreaWidth != null) {
      return (
        chartSettings.size.plotAreaWidth + chartMarginForChart.left + chartMarginForChart.right
      );
    }
    return chartSettings.size.width ?? undefined;
  }, [chartSettings.size.plotAreaWidth, chartSettings.size.width, chartMarginForChart]);

  /**
   * 右ペイン（split-plot）では親幅・親高に追従。localStorage の固定サイズは
   * 余白だけが広がる原因になるため KeisukeChart には渡さない。
   */
  const chartWidthForKeisuke = layout === 'split-plot' ? undefined : chartWidth;

  const chartHeight = useMemo(() => {
    if (chartSettings.size.plotAreaHeight != null) {
      return (
        chartSettings.size.plotAreaHeight + chartMarginForChart.top + chartMarginForChart.bottom
      );
    }
    const labelExtras = xAxisLabelExtraBottom + significanceTopExtra;
    if (useContainerHeightForPlot && autoContainerSize != null) {
      return resolveSplitPlotSvgHeightPx({
        containerHeight: autoContainerSize.height,
        containerWidth: autoContainerSize.width,
        categoryCount: Math.max(1, barData.length),
        labelExtras,
        maxPx: MAX_SPLIT_PLOT_AUTO_HEIGHT_PX,
      });
    }
    /** 高さのみ指定時は回転ラベル・有意差段用の上余白を SVG 総高さに上乗せ */
    const baseH = chartSettings.size.height ?? DEFAULT_DASHBOARD_CHART_HEIGHT_WHEN_AUTO_PX;
    return baseH + labelExtras;
  }, [
    chartSettings.size.plotAreaHeight,
    chartSettings.size.height,
    chartMarginForChart,
    xAxisLabelExtraBottom,
    significanceTopExtra,
    useContainerHeightForPlot,
    autoContainerSize,
    barData.length,
  ]);

  const legendOnRight =
    showCategoryLegend && chartSettings.legend.position === 'right';

  const handleExportSvg = useCallback(() => {
    const svg = chartContainerRef.current?.querySelector<SVGSVGElement>('[data-keisuke-chart-graph]');
    if (!svg) return;
    const clone = svg.cloneNode(true) as SVGSVGElement;
    normalizeSvgDimensionsForExport(clone);
    const svgString = new XMLSerializer().serializeToString(clone);
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${getExportBaseName()}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const handleCopySvgToClipboard = useCallback(async () => {
    const svg = chartContainerRef.current?.querySelector<SVGSVGElement>('[data-keisuke-chart-graph]');
    if (!svg) return;
    await copySvgElementAsPngToClipboard(svg);
  }, []);

  const getExportBaseName = () => `${exportFileBaseName}_${new Date().toISOString().slice(0, 10)}`;

  const renderSvgToCanvas = useCallback((callback: (canvas: HTMLCanvasElement) => void) => {
    const svg = chartContainerRef.current?.querySelector<SVGSVGElement>('[data-keisuke-chart-graph]');
    if (!svg) return;
    const clone = svg.cloneNode(true) as SVGSVGElement;
    normalizeSvgDimensionsForExport(clone);
    const svgString = new XMLSerializer().serializeToString(clone);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = new Image();
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      callback(canvas);
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }, []);

  const handleExportPng = useCallback(() => {
    renderSvgToCanvas((canvas) => {
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `${getExportBaseName()}.png`;
      a.click();
    });
  }, [renderSvgToCanvas]);

  const handleExportJpeg = useCallback(() => {
    renderSvgToCanvas((canvas) => {
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/jpeg', 0.92);
      a.download = `${getExportBaseName()}.jpg`;
      a.click();
    });
  }, [renderSvgToCanvas]);

  const handleExportTiff = useCallback(() => {
    renderSvgToCanvas((canvas) => {
      const baseName = getExportBaseName();
      try {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const w = canvas.width;
        const h = canvas.height;
        const imageData = ctx.getImageData(0, 0, w, h);
        const tiffBuffer = UTIF.encodeImage(imageData.data, w, h);
        const blob = new Blob([tiffBuffer], { type: 'image/tiff' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${baseName}.tiff`;
        a.click();
        URL.revokeObjectURL(url);
      } catch {
        const a = document.createElement('a');
        a.href = canvas.toDataURL('image/png');
        a.download = `${baseName}.png`;
        a.click();
      }
    });
  }, [renderSvgToCanvas]);

  if (layout === 'split-plot' && barData.length === 0) {
    const hasAnyNumericCell = gridData.some((row) =>
      Object.values(row).some((v) => {
        if (typeof v === 'number' && Number.isFinite(v)) return true;
        if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return true;
        return false;
      })
    );
    return (
      <div className={styles.chartWrap}>
        <div
          className={`${styles.chartRootWrapper} ${styles.chartRoot} ${isCalculating ? styles.calculating : ''}`}
          style={{
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 180,
            flexDirection: 'column',
            gap: '0.75rem',
            padding: '1rem',
            textAlign: 'center',
          }}
          aria-hidden={isCalculating}
        >
          <span className="text-muted small" style={{ maxWidth: '18rem', lineHeight: 1.45 }}>
            {hasAnyNumericCell
              ? t('keisuke.dashboard.chart.noData')
              : t('keisuke.workspace.chartNoData')}
          </span>
        </div>
      </div>
    );
  }

  const tickColorForSvg =
    chartSettings.textStyles.tickLabel.color &&
    String(chartSettings.textStyles.tickLabel.color).trim() !== ''
      ? chartSettings.textStyles.tickLabel.color
      : chartSettings.textStyles.axisLabel.color &&
          String(chartSettings.textStyles.axisLabel.color).trim() !== ''
        ? chartSettings.textStyles.axisLabel.color
        : '#000000';

  const legendTextStyle = {
    fontSize: chartSettings.legend.textStyle.fontSize,
    fontWeight: resolveFontWeightDash(
      chartSettings.legend.textStyle.fontStyle as 'normal' | 'italic' | 'bold',
      chartSettings.legend.textStyle.fontWeight as 'normal' | 'bold' | '500' | '600' | '700'
    ),
    fontStyle: resolveFontStyleDash(chartSettings.legend.textStyle.fontStyle as 'normal' | 'italic' | 'bold'),
    fontFamily: normalizeChartFontFamilyValue(chartSettings.legend.textStyle.fontFamily),
    color: tickColorForSvg,
  } as const;

  const renderLegend = (position: 'top' | 'bottom' | 'right') => {
    if (!showCategoryLegend || chartSettings.legend.position !== position) return null;
    return (
      <div
        className={`d-flex flex-wrap ${
          position === 'right'
            ? 'flex-column flex-shrink-0 align-items-start'
            : 'justify-content-center'
        } gap-3 ${position === 'bottom' ? 'mt-2' : 'mb-2'} small`}
      >
        {categories.map((name, index) => (
          <div key={name} className="d-flex align-items-center gap-2">
            <span
              style={{
                width: 10,
                height: 10,
                backgroundColor: barData.find((d) => d.name === name)?.color ?? getColor(index),
                display: 'inline-block',
                borderRadius: 2,
              }}
            />
            <span style={legendTextStyle}>{name}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderFreeLegendOverlay = () => {
    if (!showCategoryLegend || chartSettings.legend.position !== 'free') return null;
    return (
      <KeisukeFreeLegendDragOverlay
        containerRef={freeLegendAreaRef}
        offsetXPct={chartSettings.legend.freeOffsetXPct}
        offsetYPct={chartSettings.legend.freeOffsetYPct}
        onOffsetsChange={(xPct, yPct) =>
          setChartSettings((s) => ({
            ...s,
            legend: { ...s.legend, freeOffsetXPct: xPct, freeOffsetYPct: yPct },
          }))
        }
        className="d-flex flex-column gap-2 small"
        dragLabel={t('keisuke.dashboard.chart.legendDragHint')}
      >
        {categories.map((name, index) => (
          <div key={name} className="d-flex align-items-center gap-2">
            <span
              style={{
                width: 10,
                height: 10,
                backgroundColor: barData.find((d) => d.name === name)?.color ?? getColor(index),
                display: 'inline-block',
                borderRadius: 2,
              }}
            />
            <span style={legendTextStyle}>{name}</span>
          </div>
        ))}
      </KeisukeFreeLegendDragOverlay>
    );
  };

  const axisTickLabelStyleForSvg = {
    fontSize: chartSettings.textStyles.tickLabel.fontSize,
    fontWeight: resolveFontWeightDash(
      chartSettings.textStyles.tickLabel.fontStyle as 'normal' | 'italic' | 'bold',
      chartSettings.textStyles.tickLabel.fontWeight as 'normal' | 'bold' | '500' | '600' | '700'
    ),
    fontStyle: resolveFontStyleDash(chartSettings.textStyles.tickLabel.fontStyle as 'normal' | 'italic' | 'bold'),
    fontFamily: normalizeChartFontFamilyValue(chartSettings.textStyles.tickLabel.fontFamily),
    fill: tickColorForSvg,
  };

  const axisLabelStyleForSvg = {
    fontSize: chartSettings.textStyles.axisLabel.fontSize,
    fontWeight: resolveFontWeightDash(
      chartSettings.textStyles.axisLabel.fontStyle as 'normal' | 'italic' | 'bold',
      chartSettings.textStyles.axisLabel.fontWeight as 'normal' | 'bold' | '500' | '600' | '700'
    ),
    fontStyle: resolveFontStyleDash(chartSettings.textStyles.axisLabel.fontStyle as 'normal' | 'italic' | 'bold'),
    fontFamily: normalizeChartFontFamilyValue(chartSettings.textStyles.axisLabel.fontFamily),
    fill: chartSettings.textStyles.axisLabel.color,
  };

  return (
    <div
      ref={chartWrapRef}
      className={styles.chartWrap}
      style={{
        opacity: isCalculating ? 0.6 : 1,
        pointerEvents: isCalculating ? 'none' : 'auto',
        flex: '1 1 0',
        minHeight: 0,
        overflow: layout === 'split-plot' ? 'visible' : 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {layout === 'split-settings' ? (
        <KeisukeDashboardChartSettingsPanel
          chartSettings={chartSettings}
          setChartSettings={setChartSettings}
          onReset={handleResetChartSettings}
          categories={categories}
          fieldIdPrefix="keisuke-dash-bar"
        />
      ) : (
      <div ref={plotHostRef} className={styles.chartPlotHost}>
        <div className={styles.chartPlotTypeToolbar} data-keisuke-plot-type-toolbar>
          <Form.Group controlId={fid('plot-categoryKind-toolbar')} className={styles.chartPlotTypeToolbarGroup}>
            <Form.Select
              size="sm"
              name={fid('plot-categoryKind-toolbar')}
              value={categoryPlotKind}
              className={styles.chartPlotTypeSelect}
              title={t('keisuke.dashboard.chart.categoryPlotKindHint')}
              aria-label={t('keisuke.dashboard.chart.categoryPlotKind')}
              onChange={(e) =>
                setChartSettings((s) => ({
                  ...s,
                  plot: {
                    ...s.plot,
                    categoryPlotKind: e.target.value as DashboardBarChartSettings['plot']['categoryPlotKind'],
                  },
                }))
              }
            >
              <option value="bar">
                {t('keisuke.dashboard.chart.categoryPlotBar')}
              </option>
              <option value="box">
                {t('keisuke.dashboard.chart.categoryPlotBox')}
              </option>
              <option value="line">
                {t('keisuke.dashboard.chart.categoryPlotLine')}
              </option>
              <option value="scatter">
                {t('keisuke.dashboard.chart.categoryPlotScatter')}
              </option>
            </Form.Select>
          </Form.Group>
          {chartSettingsUi && !chartSettingsUi.open ? (
            <button
              type="button"
              className={styles.chartSettingsToolbarBtn}
              onClick={chartSettingsUi.toggle}
              aria-expanded={false}
              aria-controls="keisuke-chart-settings-panel"
              title={t('keisuke.workspace.showChartSettings')}
              aria-label={t('keisuke.workspace.showChartSettings')}
            >
              <i className="fas fa-sliders-h" aria-hidden />
              <span>{t('keisuke.workspace.chartSettingsBand')}</span>
            </button>
          ) : null}
        </div>
      <div
        ref={chartContainerRef}
        className={`${styles.chartRootWrapper} ${styles.chartRootWrapperNoClip} ${styles.chartRootWrapperSplitPlot}`}
      >
        <KeisukeDashboardChartExportDropdown
          onCopyClipboard={handleCopySvgToClipboard}
          onExportPng={handleExportPng}
          onExportSvg={handleExportSvg}
          onExportTiff={handleExportTiff}
          onExportJpeg={handleExportJpeg}
        />
        <div
          className={[
            styles.chartPlotRow,
            legendOnRight ? 'align-items-start align-items-sm-center gap-2' : '',
            chartWidthForKeisuke === undefined
              ? useContainerHeightForPlot
                ? styles.chartPlotRowHug
                : styles.chartPlotRowFill
              : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div
            className={[
              styles.chartPlotCol,
              chartWidthForKeisuke === undefined
                ? styles.chartPlotColFill
                : styles.chartPlotColScrollX,
            ].join(' ')}
          >
            <div
              className={[
                styles.chartPlotStack,
                chartWidthForKeisuke != null
                  ? styles.chartPlotStackFixedWidth
                  : useContainerHeightForPlot
                    ? styles.chartPlotStackHug
                    : styles.chartPlotStackFill,
              ].join(' ')}
              style={
                chartWidthForKeisuke != null ? { width: chartWidthForKeisuke } : undefined
              }
            >
            {renderLegend('top')}
            <div
              ref={freeLegendAreaRef}
              className={[
                styles.chartPlotCanvas,
                chartWidthForKeisuke === undefined
                  ? useContainerHeightForPlot
                    ? styles.chartPlotCanvasHug
                    : styles.chartPlotCanvasFill
                  : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={
                useContainerHeightForPlot && chartWidthForKeisuke === undefined
                  ? { height: chartHeight }
                  : undefined
              }
            >
            <KeisukeChart
              height={chartHeight}
              width={chartWidthForKeisuke}
              margin={chartMarginForChart}
              backgroundColor={chartSettings.chartBackgroundColor || 'transparent'}
              plotAreaBackgroundColor={chartSettings.chartBackgroundColor || 'transparent'}
            >
              {(dimensions: ChartDimensions) => {
                const plotAreaBounds = {
                  x: dimensions.margin.left,
                  y: dimensions.margin.top,
                  width: dimensions.width - dimensions.margin.left - dimensions.margin.right,
                  height: dimensions.height - dimensions.margin.top - dimensions.margin.bottom,
                };
                const yLenPct = Math.min(
                  100,
                  Math.max(30, chartSettings.yAxis.lengthPercent ?? 100)
                );
                const yAxisLengthPx = (plotAreaBounds.height * yLenPct) / 100;
                const yBandStart =
                  plotAreaBounds.y + (plotAreaBounds.height - yAxisLengthPx) / 2;
                const valueBandBounds = {
                  x: plotAreaBounds.x,
                  y: yBandStart,
                  width: plotAreaBounds.width,
                  height: yAxisLengthPx,
                };
                /** X軸カテゴリ帯の幅の上限（設定％）。実際の帯幅は棒列に合わせてこれ以下に縮む */
                const axisLengthMax =
                  (plotAreaBounds.width * chartSettings.xAxis.lengthPercent) / 100;
                const barCount = categories.length;
                const baseBarWidth = chartSettings.plot.barWidth;
                const maxGapPx =
                  barCount > 0
                    ? (axisLengthMax - barCount * baseBarWidth) / (barCount + 1)
                    : 0;
                let effectiveBarWidth = baseBarWidth;
                let gapPx = 0;
                if (maxGapPx < 0 && barCount > 0) {
                  effectiveBarWidth = axisLengthMax / barCount;
                  gapPx = 0;
                } else {
                  gapPx = Math.max(0, maxGapPx) * chartSettings.plot.barSpacing;
                }
                const axisLength = shrinkBarCategoryAxisLength(
                  barCount,
                  axisLengthMax,
                  gapPx,
                  effectiveBarWidth
                );
                const xBandStart = resolveCategoryBandStartX(
                  plotAreaBounds.x,
                  plotAreaBounds.width,
                  axisLength,
                  axisLengthMax
                );
                const axisStartX = xBandStart;
                const axisEndX = xBandStart + axisLength;
                const xScaleAdjusted = (value: string) => {
                  const index = categories.indexOf(value);
                  return gapPx + effectiveBarWidth / 2 + index * (effectiveBarWidth + gapPx);
                };
                const barYDomain = yDomain;
                /** min/max 上書きで [大, 小] になり得るため、数値域の端は常にここから取る */
                const barYMin = Math.min(barYDomain[0], barYDomain[1]);
                const barYMax = Math.max(barYDomain[0], barYDomain[1]);
                const useLogScale =
                  chartSettings.yAxis.scaleType === 'log' &&
                  barYMin > 0 &&
                  barYMax > 0;
                const useSqrtScale =
                  chartSettings.yAxis.scaleType === 'sqrt' &&
                  Number.isFinite(barYDomain[0]) &&
                  Number.isFinite(barYDomain[1]) &&
                  barYMax > 0;
                const useInvScale =
                  chartSettings.yAxis.scaleType === 'reciprocal' &&
                  barYMin > 0 &&
                  barYMax > 0;
                const logDomain = useLogScale
                  ? (() => {
                      const rawMin = chartSettings.yAxis.minValue ?? barYMin;
                      const rawMax = chartSettings.yAxis.maxValue ?? barYMax;
                      const safeMin = rawMin > 0 ? rawMin : barYMin;
                      const safeMax = rawMax > 0 ? rawMax : barYMax;
                      const minValue = Math.min(safeMin, safeMax);
                      const maxValue = Math.max(safeMin, safeMax);
                      const adjustedMax = minValue === maxValue ? maxValue * 10 : maxValue;
                      const logMin = Math.log10(minValue);
                      const logMax = Math.log10(adjustedMax);
                      const logRange = Math.max(0.5, logMax - logMin);
                      return [logMin, logMax + logRange * 0.1] as [number, number];
                    })()
                  : null;
                const sqrtDomain = useSqrtScale
                  ? (() => {
                      const rawMin = chartSettings.yAxis.minValue ?? Math.max(0, barYMin);
                      const rawMax = chartSettings.yAxis.maxValue ?? barYMax;
                      let minV = Math.max(0, Math.min(rawMin, rawMax));
                      let maxV = Math.max(0, Math.max(rawMin, rawMax));
                      if (maxV < minV) [minV, maxV] = [maxV, minV];
                      if (maxV === minV) maxV = minV + 1e-9;
                      const sMin = Math.sqrt(minV);
                      const sMax = Math.sqrt(maxV);
                      const sRange = Math.max(1e-9, sMax - sMin);
                      return [sMin, sMax + sRange * 0.1] as [number, number];
                    })()
                  : null;
                const invDomain = useInvScale
                  ? (() => {
                      const rawMin = chartSettings.yAxis.minValue ?? barYMin;
                      const rawMax = chartSettings.yAxis.maxValue ?? barYMax;
                      const safeMin = rawMin > 0 ? rawMin : barYMin;
                      const safeMax = rawMax > 0 ? rawMax : barYMax;
                      let minV = Math.min(safeMin, safeMax);
                      let maxV = Math.max(safeMin, safeMax);
                      if (maxV < minV) [minV, maxV] = [maxV, minV];
                      if (maxV === minV) maxV = minV * 1.01 + 1e-12;
                      const invLow = 1 / maxV;
                      const invHigh = 1 / minV;
                      const invRange = Math.max(1e-15, invHigh - invLow);
                      const low = Math.max(1e-15, invLow - invRange * 0.05);
                      const high = invHigh + invRange * 0.1;
                      return [low, high] as [number, number];
                    })()
                  : null;
                const yScaleDomain = useLogScale
                  ? (logDomain as [number, number])
                  : useSqrtScale
                    ? (sqrtDomain as [number, number])
                    : useInvScale
                      ? (invDomain as [number, number])
                      : barYDomain;
                const yScaleAdjustedLinear = createLinearScale(yScaleDomain, [0, yAxisLengthPx]);
                const yScaleValue = (value: number) => {
                  if (useLogScale) {
                    const safeValue = Math.max(value, barYMin);
                    return yScaleAdjustedLinear(Math.log10(safeValue));
                  }
                  if (useSqrtScale) {
                    const safeValue = Math.max(0, value);
                    return yScaleAdjustedLinear(Math.sqrt(safeValue));
                  }
                  if (useInvScale) {
                    const safeValue = Math.max(value, barYMin);
                    const inv = 1 / safeValue;
                    if (!Number.isFinite(inv)) {
                      return yScaleAdjustedLinear(yScaleDomain[0]);
                    }
                    return yScaleAdjustedLinear(inv);
                  }
                  return yScaleAdjustedLinear(value);
                };
                const yTicks = useLogScale
                  ? calculateNiceTicks(yScaleDomain, 6)
                  : useSqrtScale
                    ? calculateNiceTicks(yScaleDomain, 6)
                    : useInvScale
                      ? calculateNiceTicks(yScaleDomain, 6)
                      : calculateTicksByInterval(barYDomain, chartSettings.yAxis.tickInterval);
                const yAxisScale: AxisScale = {
                  domain: yScaleDomain,
                  range: [0, yAxisLengthPx],
                  ticks: yTicks,
                };
                const yAxisTickFormatter = createYAxisTickFormatter(
                  chartSettings.yAxis,
                  useLogScale,
                  useSqrtScale,
                  useInvScale
                );

                const xGridDash =
                  chartSettings.xAxis.grid.lineStyle === 'dashed'
                    ? '4,4'
                    : chartSettings.xAxis.grid.lineStyle === 'dotted'
                      ? '2,2'
                      : 'none';

                const plotBottom = valueBandBounds.y + valueBandBounds.height;
                const plotTop = valueBandBounds.y;
                const xIv = chartSettings.xAxis.intersectionValue;
                let axisLineY = plotBottom;
                if (xIv !== null && Number.isFinite(xIv)) {
                  const candidate = plotBottom - yScaleValue(xIv);
                  axisLineY = Math.min(plotBottom, Math.max(plotTop, candidate));
                }
                const xAxisStroke = tickColorForSvg;

                const yPixel = (v: number) =>
                  valueBandBounds.y + valueBandBounds.height - yScaleValue(v);
                const maxBarTop =
                  categoryPlotKind === 'box' && boxPlotSeries.length > 0
                    ? Math.max(0, ...boxPlotSeries.flatMap((b) => [b.whiskerHigh, ...b.outliers]))
                    : categoryPlotKind === 'scatter' && scatterPoints.length > 0
                      ? Math.max(0, ...scatterPoints.map((p) => p.value))
                      : Math.max(...barData.map((d) => d.errorBar?.upper ?? d.value), 0);
                const significanceRowPitchPx = getDashboardSignificanceRowPitchPx(chartSettings);
                const maxBarTopPx =
                  valueBandBounds.y + valueBandBounds.height - yScaleValue(maxBarTop);
                const marginAboveErrorBar = 12;

                const tickLength = 5;
                const labelPad = 15;
                const categoryLabelCenterY = axisLineY + tickLength + labelPad;
                const categoryLabelsBottomY = getCategoryTickLabelsBottomY(
                  categoryLabelCenterY,
                  categories,
                  chartSettings.textStyles.tickLabel.fontSize,
                  chartSettings.xAxis.tickLabelRotation
                );
                const xAxisTitleY =
                  categoryLabelsBottomY + chartSettings.xAxis.labelPosition;

                return (
                  <>
                    <KeisukeAxis
                      bounds={valueBandBounds}
                      dimensions={dimensions}
                      type="y"
                      scale={yAxisScale}
                      label={chartSettings.yAxis.label}
                      showGrid={chartSettings.yAxis.grid.show}
                      gridLineStyle={chartSettings.yAxis.grid.lineStyle}
                      gridOpacity={chartSettings.yAxis.grid.opacity}
                      gridColor={chartSettings.yAxis.grid.color}
                      axisLineWidth={chartSettings.yAxis.lineWidth}
                      yAxisLabelPosition={chartSettings.yAxis.labelPosition}
                      intersectionValue={chartSettings.yAxis.intersectionValue ?? undefined}
                      intersectionDomain={[0, Math.max(0, categories.length - 1)]}
                      tickFormatter={yAxisTickFormatter}
                      gridLength={axisLength}
                      yAxisLineX={xBandStart}
                      axisLabelStyle={axisLabelStyleForSvg}
                      tickLabelStyle={axisTickLabelStyleForSvg}
                    />
                    <g>
                      {chartSettings.xAxis.grid.show &&
                        categories.map((cat) => {
                          const gx = xBandStart + xScaleAdjusted(cat);
                          return (
                            <line
                              key={`xgrid-${cat}`}
                              x1={gx}
                              y1={valueBandBounds.y}
                              x2={gx}
                              y2={valueBandBounds.y + valueBandBounds.height}
                              stroke={chartSettings.xAxis.grid.color}
                              strokeWidth="0.5"
                              strokeDasharray={xGridDash}
                              opacity={chartSettings.xAxis.grid.opacity}
                            />
                          );
                        })}
                      <line
                        x1={axisStartX}
                        y1={axisLineY}
                        x2={axisEndX}
                        y2={axisLineY}
                        stroke={xAxisStroke}
                        strokeWidth={chartSettings.xAxis.lineWidth}
                      />
                      {categories.map((category) => {
                        const x = xBandStart + xScaleAdjusted(category);
                        const axisY = axisLineY;
                        const labelY = categoryLabelCenterY;
                        const labelRotation = chartSettings.xAxis.tickLabelRotation;
                        const labelHasRotation = labelRotation !== 0;
                        const labelAnchor = chartSettings.xAxis.tickLabelAnchor;
                        return (
                          <g key={category}>
                            <line
                              x1={x}
                              y1={axisY}
                              x2={x}
                              y2={axisY + tickLength}
                              stroke={xAxisStroke}
                              strokeWidth={chartSettings.xAxis.lineWidth}
                            />
                            <text
                              x={x}
                              y={labelY}
                              textAnchor={labelAnchor}
                              dominantBaseline="middle"
                              fontSize={chartSettings.textStyles.tickLabel.fontSize}
                              fill={tickColorForSvg}
                              fontWeight={axisTickLabelStyleForSvg.fontWeight}
                              fontStyle={axisTickLabelStyleForSvg.fontStyle}
                              fontFamily={axisTickLabelStyleForSvg.fontFamily}
                              transform={
                                labelHasRotation
                                  ? `rotate(${labelRotation}, ${x}, ${labelY})`
                                  : undefined
                              }
                            >
                              {category}
                            </text>
                          </g>
                        );
                      })}
                      {chartSettings.xAxis.label && (
                        <text
                          x={axisStartX + axisLength / 2}
                          y={xAxisTitleY}
                          dominantBaseline="hanging"
                          textAnchor="middle"
                          fontSize={chartSettings.textStyles.axisLabel.fontSize}
                          fill={chartSettings.textStyles.axisLabel.color}
                          fontWeight={axisLabelStyleForSvg.fontWeight}
                          fontStyle={axisLabelStyleForSvg.fontStyle}
                          fontFamily={axisLabelStyleForSvg.fontFamily}
                        >
                          {chartSettings.xAxis.label}
                        </text>
                      )}
                    </g>
                    {(() => {
                      const rl = coalesceReferenceLine(chartSettings.referenceLine);
                      if (!rl.enabled || rl.y == null || !Number.isFinite(rl.y)) return null;
                      if (useLogScale && rl.y <= 0) return null;
                      if (useInvScale && rl.y <= 0) return null;
                      if (useSqrtScale && rl.y < 0) return null;
                      const py = yPixel(rl.y);
                      const yTop = valueBandBounds.y;
                      const yBot = valueBandBounds.y + valueBandBounds.height;
                      if (py < yTop - 2 || py > yBot + 2) return null;
                      return (
                        <line
                          key="keisuke-y-reference-line"
                          data-keisuke-y-reference-line
                          x1={xBandStart}
                          x2={axisEndX}
                          y1={py}
                          y2={py}
                          stroke={tickColorForSvg}
                          strokeWidth={1.25}
                          strokeDasharray="10,5"
                          opacity={0.92}
                        />
                      );
                    })()}
                    {(() => {
                      const rl = coalesceReferenceLine(chartSettings.referenceLine);
                      if (!rl.verticalEnabled || !rl.verticalCategory) return null;
                      if (!categories.includes(rl.verticalCategory)) return null;
                      const vx = xBandStart + xScaleAdjusted(rl.verticalCategory);
                      const xLeft = xBandStart - effectiveBarWidth * 0.6;
                      const xRight = axisEndX + effectiveBarWidth * 0.6;
                      if (vx < xLeft || vx > xRight) return null;
                      return (
                        <line
                          key="keisuke-x-reference-line"
                          data-keisuke-x-reference-line
                          x1={vx}
                          x2={vx}
                          y1={valueBandBounds.y}
                          y2={valueBandBounds.y + valueBandBounds.height}
                          stroke={tickColorForSvg}
                          strokeWidth={1.25}
                          strokeDasharray="6,4"
                          opacity={0.92}
                        />
                      );
                    })()}
                    {categoryPlotKind === 'box' && boxPlotSeries.length > 0 ? (
                      <g data-keisuke-dash-boxplots>
                        {boxPlotSeries.map((b) => {
                          const cx = xBandStart + xScaleAdjusted(b.name);
                          const bw = Math.min(
                            Math.max(6, effectiveBarWidth * 0.45),
                            effectiveBarWidth * 0.85
                          );
                          const stroke = b.color ?? tickColorForSvg;
                          const fill = chartSettings.chartBackgroundColor === 'transparent' ? '#ffffff' : chartSettings.chartBackgroundColor || '#ffffff';
                          const yLow = yPixel(b.whiskerLow);
                          const yHigh = yPixel(b.whiskerHigh);
                          const yQ1 = yPixel(b.q1);
                          const yQ3 = yPixel(b.q3);
                          const yMed = yPixel(b.median);
                          const boxTop = Math.min(yQ1, yQ3);
                          const boxH = Math.abs(yQ3 - yQ1);
                          return (
                            <g key={b.name}>
                              <line
                                x1={cx}
                                y1={yHigh}
                                x2={cx}
                                y2={yLow}
                                stroke={stroke}
                                strokeWidth={1.5}
                              />
                              <line
                                x1={cx - bw / 2}
                                y1={yHigh}
                                x2={cx + bw / 2}
                                y2={yHigh}
                                stroke={stroke}
                                strokeWidth={1.5}
                              />
                              <line
                                x1={cx - bw / 2}
                                y1={yLow}
                                x2={cx + bw / 2}
                                y2={yLow}
                                stroke={stroke}
                                strokeWidth={1.5}
                              />
                              <rect
                                x={cx - bw / 2}
                                y={boxTop}
                                width={bw}
                                height={Math.max(boxH, 1)}
                                fill={fill}
                                stroke={stroke}
                                strokeWidth={1.5}
                              />
                              <line
                                x1={cx - bw / 2}
                                y1={yMed}
                                x2={cx + bw / 2}
                                y2={yMed}
                                stroke={stroke}
                                strokeWidth={2}
                              />
                              {b.outliers.map((o, oi) => (
                                <circle
                                  key={`${b.name}-o-${oi}`}
                                  cx={cx}
                                  cy={yPixel(o)}
                                  r={3.5}
                                  fill={stroke}
                                  stroke={stroke}
                                  strokeWidth={1}
                                />
                              ))}
                            </g>
                          );
                        })}
                      </g>
                    ) : categoryPlotKind === 'line' && barData.length > 0 ? (
                      <g data-keisuke-dash-linechart>
                        {barData.map((d) => {
                          const cx = xBandStart + xScaleAdjusted(d.name);
                          if (!d.errorBar) return null;
                          return (
                            <g key={`eb-${d.name}`}>
                              <line
                                x1={cx}
                                y1={yPixel(d.errorBar.upper)}
                                x2={cx}
                                y2={yPixel(d.errorBar.lower)}
                                stroke={tickColorForSvg}
                                strokeWidth={1.5}
                              />
                              <line
                                x1={cx - 5}
                                y1={yPixel(d.errorBar.upper)}
                                x2={cx + 5}
                                y2={yPixel(d.errorBar.upper)}
                                stroke={tickColorForSvg}
                                strokeWidth={1.5}
                              />
                              <line
                                x1={cx - 5}
                                y1={yPixel(d.errorBar.lower)}
                                x2={cx + 5}
                                y2={yPixel(d.errorBar.lower)}
                                stroke={tickColorForSvg}
                                strokeWidth={1.5}
                              />
                            </g>
                          );
                        })}
                        {barData.length >= 2 && (
                          <polyline
                            fill="none"
                            stroke={tickColorForSvg}
                            strokeWidth={2}
                            points={barData
                              .map((d) => {
                                const cx = xBandStart + xScaleAdjusted(d.name);
                                const cy = yPixel(d.value);
                                return `${cx},${cy}`;
                              })
                              .join(' ')}
                          />
                        )}
                        {barData.map((d) => {
                          const cx = xBandStart + xScaleAdjusted(d.name);
                          const cy = yPixel(d.value);
                          const fill = d.color ?? tickColorForSvg;
                          return (
                            <circle
                              key={`pt-${d.name}`}
                              cx={cx}
                              cy={cy}
                              r={4.5}
                              fill={fill}
                              stroke={tickColorForSvg}
                              strokeWidth={1.2}
                            />
                          );
                        })}
                      </g>
                    ) : categoryPlotKind === 'scatter' && scatterPoints.length > 0 ? (
                      <g data-keisuke-dash-scatter>
                        {scatterPoints.map((pt, i) => {
                          const maxJitter = Math.min(effectiveBarWidth * 0.38, 16);
                          const seed = Math.sin(pt.value * 12.9898 + i * 78.233) * 43758.5453;
                          const jitter = (seed - Math.floor(seed) - 0.5) * 2 * maxJitter;
                          const cx = xBandStart + xScaleAdjusted(pt.group) + jitter;
                          const cy = yPixel(pt.value);
                          return (
                            <circle
                              key={`sp-${pt.group}-${i}`}
                              cx={cx}
                              cy={cy}
                              r={3.5}
                              fill={pt.color ?? tickColorForSvg}
                              fillOpacity={0.6}
                              stroke={tickColorForSvg}
                              strokeWidth={0.8}
                            />
                          );
                        })}
                        {barData.map((d) => {
                          const cx = xBandStart + xScaleAdjusted(d.name);
                          const cy = yPixel(d.value);
                          const hw = Math.min(effectiveBarWidth * 0.38, 18);
                          return (
                            <g key={`smean-${d.name}`}>
                              {d.errorBar && (
                                <g>
                                  <line x1={cx} y1={yPixel(d.errorBar.upper)} x2={cx} y2={yPixel(d.errorBar.lower)} stroke={tickColorForSvg} strokeWidth={1.5} />
                                  <line x1={cx - 5} y1={yPixel(d.errorBar.upper)} x2={cx + 5} y2={yPixel(d.errorBar.upper)} stroke={tickColorForSvg} strokeWidth={1.5} />
                                  <line x1={cx - 5} y1={yPixel(d.errorBar.lower)} x2={cx + 5} y2={yPixel(d.errorBar.lower)} stroke={tickColorForSvg} strokeWidth={1.5} />
                                </g>
                              )}
                              <line x1={cx - hw} y1={cy} x2={cx + hw} y2={cy} stroke={tickColorForSvg} strokeWidth={2.5} />
                            </g>
                          );
                        })}
                      </g>
                    ) : (
                      <KeisukeBar
                        bounds={{ ...valueBandBounds, x: xBandStart, width: axisLength }}
                        data={barData}
                        xScale={xScaleAdjusted}
                        yScale={(v: number) =>
                          valueBandBounds.y + valueBandBounds.height - yScaleValue(v)
                        }
                        barWidth={effectiveBarWidth}
                        barSpacing={0}
                        errorBarColor={tickColorForSvg}
                        barStrokeColor={tickColorForSvg}
                      />
                    )}
                    {chartSettings.significance.show &&
                      significanceMarks.length > 0 &&
                      significanceMarks.map((m) => {
                        const x1 = xBandStart + xScaleAdjusted(categories[m.leftIndex]);
                        const x2 = xBandStart + xScaleAdjusted(categories[m.rightIndex]);
                        const lineY =
                          maxBarTopPx -
                          marginAboveErrorBar -
                          chartSettings.significance.offset -
                          m.level * significanceRowPitchPx;
                        const tickFontSize = chartSettings.textStyles.tickLabel.fontSize || 16;
                        /** 上余白内に * ラベルが収まる下限（SVG 上端との衝突回避） */
                        const minYForText = 4 + tickFontSize + 2;
                        const lineYClamped = Math.max(
                          minYForText,
                          Math.min(valueBandBounds.y + valueBandBounds.height - 20, lineY)
                        );
                        const labelText =
                          chartSettings.significance.style === 'p' ? formatPValue(m.pValue) : m.symbol;
                        const sigColor = tickColorForSvg;
                        return (
                          <g key={`${m.leftIndex}-${m.rightIndex}-${m.level}`}>
                            <line
                              x1={x1}
                              y1={lineYClamped}
                              x2={x2}
                              y2={lineYClamped}
                              stroke={sigColor}
                              strokeWidth={1}
                            />
                            <line
                              x1={x1}
                              y1={lineYClamped}
                              x2={x1}
                              y2={lineYClamped + 6}
                              stroke={sigColor}
                              strokeWidth={1}
                            />
                            <line
                              x1={x2}
                              y1={lineYClamped}
                              x2={x2}
                              y2={lineYClamped + 6}
                              stroke={sigColor}
                              strokeWidth={1}
                            />
                            <text
                              x={(x1 + x2) / 2}
                              y={lineYClamped - 4}
                              textAnchor="middle"
                              fontSize={tickFontSize}
                              fill={sigColor}
                              fontWeight="bold"
                            >
                              {labelText}
                            </text>
                          </g>
                        );
                      })}
                  </>
                );
              }}
            </KeisukeChart>
            {renderFreeLegendOverlay()}
            </div>
            {renderLegend('bottom')}
            </div>
          </div>
          {renderLegend('right')}
        </div>
      </div>
      </div>
      )}
    </div>
  );
}
